// src/utils/colorUtils.js
/**
 * @module colorUtils
 * Utility functions for assigning colors to tokens for visualization purposes.
 * Provides both palette-based and spectrum-based coloring strategies.
 */

/**
 * Predefined color palette for token-ID-based coloring.
 * Each color is a hex string. The palette is cycled through using modulo arithmetic,
 * so any token ID will always map to a color in this array.
 */
const tokenColorPalette = [
  "#E31A1C", // Red
  "#D95F02", // Orange
  "#FF7F00", // Bright Orange
  "#FDBF6F", // Light Orange
  "#FFFF99", // Yellow
  "#B2DF8A", // Light Green
  "#33A02C", // Green
  "#1B9E77", // Teal
  "#A6CEE3", // Light Blue
  "#1F78B4", // Blue
  "#7570B3", // Purple
  "#6A3D9A", // Dark Purple
  "#CAB2D6", // Lavender
  "#E7298A", // Pink
  "#FB9A99", // Light Red
  "#B15928", // Brown
];

/**
 * Returns a deterministic color for a token based on its ID.
 * This ensures that the same token ID always maps to the same color,
 * which is useful for visually grouping identical tokens.
 *
 * @param {number} id - Token identifier (should be a non-negative integer).
 * @returns {string} Hex color string from the palette.
 *
 * Example:
 *   getTokenColor(3); // "#D95F02"
 */
export function getTokenColor(id: number): string {
  return tokenColorPalette[id % tokenColorPalette.length];
}

/**
 * Predefined color palette for source-text-ID-based coloring.
 * Each color is a hex string. Provides visually distinct colors for multi-corpus overlays.
 */
const sourceColorPalette = [
  "#E63946", // Red
  "#2A9D8F", // Teal
  "#E9C46A", // Yellow
  "#F4A261", // Orange
  "#264653", // Dark Blue-Green
  "#A8DADC", // Light Cyan
  "#457B9D", // Blue
  "#6A4C93", // Purple
  "#1982C4", // Bright Blue
  "#8AC926", // Yellow-Green
  "#FF595E", // Coral
  "#6A0572", // Dark Purple
];

/**
 * Map a source text ID string to a stable palette index using a djb2 hash.
 * @param {string} str
 * @returns {number}
 */
function sourceIdToIndex(str: string): number {
  if (!str) return 0;
  let hash = 5381;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) + hash) + str.charCodeAt(i);
    hash = hash & hash; // Convert to 32-bit int
  }
  return Math.abs(hash) % sourceColorPalette.length;
}

/**
 * Returns a deterministic color for a source text based on its ID.
 * Useful for visually distinguishing chunks from different source texts
 * in a multi-corpus visualization.
 *
 * @param {string} sourceTextId - Source text identifier (typically a UUID).
 * @returns {string} Hex color string from the palette.
 *
 * Example:
 *   getSourceColor('abc-123'); // "#2A9D8F"
 */
export function getSourceColor(sourceTextId: string): string {
  return sourceColorPalette[sourceIdToIndex(sourceTextId)];
}

/**
 * Returns an HSL color along a blue-to-red spectrum based on the token's sequence index.
 * This is useful for visualizing the order of tokens, such as in a sentence or document.
 * The first token is blue (hue 240), the last is red (hue 0), and others are interpolated.
 *
 * @param {number} index - Zero-based token index in the sequence.
 * @param {number} total - Total number of tokens in the sequence.
 * @returns {string} HSL color string representing the token's position.
 *
 * Example:
 *   getSequenceColor(0, 5); // "hsl(240,100%,50%)" (blue)
 *   getSequenceColor(4, 5); // "hsl(0,100%,50%)" (red)
 */
export function getSequenceColor(index: number, total: number): string {
  if (total <= 1) {
    // If only one token, fall back to the palette's first color
    return getTokenColor(0);
  }
  // Map index 0→blue (240°) to index total-1→red (0°)
  const hue = 240 - (240 * index) / (total - 1);
  return `hsl(${hue},100%,75%)`;
}
